package models

import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.serialization.Serializable

const val PUBLIC_GAME_MAX_PLAYERS = 8
const val PUBLIC_GAME_VISIBLE_SECONDS = 5L * 60

fun isPublicListingFresh(publicSinceSeconds: Long?, nowSeconds: Long): Boolean =
    publicSinceSeconds != null && publicSinceSeconds >= nowSeconds - PUBLIC_GAME_VISIBLE_SECONDS

@Serializable
data class DomainGame(
    val dateLastModified: String? = null,
    val joinCode: String = "",
    val players: List<Player> = emptyList(),
    val round: Round? = null,
    val endRoundNum: Int = 0,
    val orderedPlayerNames: List<String> = emptyList(),
    val host: String = "",
    val hostUid: String = "",
    val currentPlayer: String = "",
    val settings: List<Setting> = emptyList(),
    val finished : Boolean = false,
    val nudge: Nudge? = null,
    // True when the host's Plus perks should apply to this lobby, including ad-free play for guests.
    val hostPerksActive: Boolean = false,
    // Public games are discoverable from the home screen until the host starts them. Invite-only
    // games still use the same join code, but never appear in discovery.
    val isPublic: Boolean = false,
    val publicSinceSeconds: Long? = null,
    val participantUids: List<String> = emptyList(),
    val recapStats: GameRecapStats = GameRecapStats(),
)

@Serializable
data class FirebaseGame(
    val dateLastModified: Timestamp? = null,
    // When this game should be auto-deleted by Firestore TTL. Refreshed on every mutation so an
    // actively-played game never expires; abandoned games disappear ~6h after the last move. Drives
    // the TTL policy on the `games` collection. See RemoteGameDataSource.gameExpiry().
    val expiresAt: Timestamp? = null,
    val joinCode: String = "",
    val round: Round? = null,
    val endRoundNum: Int = 0,
    val orderedPlayerNames: List<String> = emptyList(),
    val host: String = "",
    val hostUid: String = "",
    val currentPlayer: String = "",
    val settings: List<Setting> = emptyList(),
    val finished : Boolean = false,
    val nudge: Nudge? = null,
    val hostPerksActive: Boolean = false,
    val isPublic: Boolean = false,
    // Last public-lobby activity. Used to hide abandoned public lobbies and put active ones first.
    val publicSince: Timestamp? = null,
    val participantUids: List<String> = emptyList(),
    // Bounded post-game highlights. This is outcome metadata, not an unbounded turn history.
    val recapStats: GameRecapStats = GameRecapStats(),
    // Set by the client during a participant self-leave so Firestore rules can verify which
    // player doc is being removed without scanning the roster.
    val lastLeftPlayer: String = "",
    // Schema/behavior version of the client that created this game (GAME_PROTOCOL_VERSION). Joins
    // from clients speaking a lower version are refused with an update prompt. Games created before
    // the field existed default to 1.
    val protocolVersion: Int = 1,
)

/** The small, privacy-conscious projection shown in the public game browser. */
data class PublicGameSummary(
    val joinCode: String,
    val hostName: String,
    val playerCount: Int,
    val roundCount: Int,
    val ginkoEnabled: Boolean,
    val hostPerksActive: Boolean,
    val publishedAtSeconds: Long,
)

enum class JoinGameResult {
    JOINED,
    NOT_FOUND,
    ALREADY_STARTED,
    NAME_TAKEN,
    FULL,
    EXPIRED,
    /** The game was created by a newer app version this client can't correctly play. */
    VERSION_TOO_OLD,
}

/**
 * A buzz sent to [targetPlayer]'s device. [nonce] strictly increases so each nudge fires its haptic
 * exactly once; observers ignore nonces they've already seen (and the one present when they joined).
 */
@Serializable
data class Nudge(
    val targetPlayer: String = "",
    // Int (not Long): on Kotlin/JS a Long is a boxed object that Firestore rejects, and a per-game
    // nudge counter never needs 64 bits.
    val nonce: Int = 0,
)

/**
 * The intentionally small vocabulary for table reactions. These are quick, non-verbal signals —
 * not messages — so the game still feels like people reacting around one table.
 */
@Serializable
enum class TableReactionKind {
    WATCHING,
    BANK,
    ROLL,
    SHOCK,
    FIRE,
}

/**
 * One replaceable reaction event per signed-in device. Remote storage keys these documents by the
 * sender's auth uid, so a reaction overwrites that sender's previous event instead of building a
 * chat-like history. [eventId] lets observers distinguish the replacement from the initial stale
 * document they may see when opening a game.
 */
@Serializable
data class TableReaction(
    val eventId: String = "",
    val senderPlayer: String = "",
    val targetPlayer: String = "",
    val kind: TableReactionKind = TableReactionKind.WATCHING,
    val roundNum: Int = 0,
    val currentRoll: Int = 0,
    val ownerUid: String = "",
    val createdAt: Timestamp? = null,
    // Allows a collection-group TTL policy to reap orphaned reaction docs if the parent game is
    // deleted by its own TTL rather than through RemoteGameDataSource.deleteGame().
    val expiresAt: Timestamp? = null,
)

@Serializable
data class Setting(
    val name: String = "",
    val value: String = "",
)

// @Entity(
//     tableName = "player",
// )
@Serializable
data class Player(
    // @PrimaryKey
    val name: String = "",
    val points: Int = 0,
    val hostCreated: Boolean = false,
    val isBot: Boolean = false,
    // Set once when the player is created/joins (now + 6h). Drives the TTL policy on the `players`
    // collection group so player docs don't outlive a deleted game. Not refreshed during play, so
    // Player objects stay stable across rounds (no recomposition churn). Null for local-only games.
    val expiresAt: Timestamp? = null,
    // Cosmetic dice skin id (see DiceSkin). Synced so every device renders the current roller's dice
    // in their chosen style. Blank = the default skin.
    val diceSkin: String = "",
    // Cosmetic dice hat id (see DiceHat) — the on-top layer. Blank = no hat.
    val diceHat: String = "",
    // Anonymous Firebase Auth uid that owns/controls this player doc. Host-created bots/players use
    // the host's uid, so security rules can distinguish lobby participants from random clients.
    val ownerUid: String = "",
)

@Serializable
data class Round(
    val roundNum: Int = 0,
    val currentPoints: Int = 0,
    val dieOne: Int = 0,
    val dieTwo: Int = 0,
    val currentRoll: Int = 0,
    val activeOrderedPlayerNames: List<String> = emptyList(),
    val modifier: RoundModifier = RoundModifier.NONE,
    val ginkoEvent: GinkoEvent = GinkoEvent.NONE,
    val ginkoEventsTriggered: Int = 0,
    val bankLockoutTurnsRemaining: Int = 0,
    // Who rolled the dice currently shown. currentPlayer has already advanced to the next player by
    // the time these dice render, so the cosmetic (skin/effect) is keyed off this, not currentPlayer.
    val rolledBy: String = "",
)
